/**
 * Serviço de OAuth 2.0 para Google Calendar
 * Gerencia autenticação e tokens de acesso
 */

import { database } from '../config/backend';
import { ref, get, set, remove } from './phpDataAdapter';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_REDIRECT_URI = process.env.REACT_APP_GOOGLE_REDIRECT_URI || window.location.origin + '/oauth/google/callback';
const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events';

// URL base da API (ajuste conforme necessário)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export interface GoogleCalendarToken {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  expires_at: number;
  token_type: string;
  scope: string;
}

export interface GoogleCalendarAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Gera URL de autorização OAuth 2.0
 */
export function generateAuthUrl(userId: string): string {
  const state = btoa(JSON.stringify({ userId, timestamp: Date.now() }));
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: GOOGLE_REDIRECT_URI,
    response_type: 'code',
    scope: GOOGLE_SCOPES,
    access_type: 'offline', // Necessário para obter refresh_token
    prompt: 'consent', // Força o consentimento para garantir refresh_token para todos os usuários
    include_granted_scopes: 'true', // Inclui escopos já concedidos
    state,
  });

  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

/**
 * Troca código de autorização por token de acesso
 * Usa endpoint PHP seguro no backend para manter client_secret protegido
 */
export async function exchangeCodeForToken(code: string, state: string): Promise<GoogleCalendarToken> {
  try {
    const stateData = JSON.parse(atob(state));
    const userId = stateData.userId;

    // Usar endpoint PHP seguro no backend
    const response = await fetch(`${API_BASE_URL}/google-oauth-token.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        redirect_uri: GOOGLE_REDIRECT_URI,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error_description || error.error || 'Erro ao obter token');
    }

    const data = await response.json();
    
    // Verificar se refresh_token foi retornado (necessário para renovação)
    if (!data.refresh_token) {
      console.warn('⚠️ Refresh token não retornado pelo Google. O usuário pode precisar reconectar.');
      // Se não houver refresh_token, ainda podemos salvar o access_token
      // mas o usuário precisará reconectar quando expirar
    }

    const token: GoogleCalendarToken = {
      access_token: data.access_token,
      refresh_token: data.refresh_token || undefined, // Pode ser undefined se não retornado
      expires_in: data.expires_in,
      expires_at: Date.now() + (data.expires_in * 1000),
      token_type: data.token_type || 'Bearer',
      scope: data.scope,
    };

    // Salvar token no servidor PHP/MySQL
    await saveToken(userId, token);

    return token;
  } catch (error: any) {
    console.error('Erro ao trocar código por token:', error);
    throw new Error(error.message || 'Erro ao autenticar com Google Calendar');
  }
}

/**
 * Salva token no servidor PHP/MySQL
 */
async function saveToken(userId: string, token: GoogleCalendarToken): Promise<void> {
  try {
    await set(ref(database, `users/${userId}/googleCalendarToken`), {
      ...token,
      // Não salvar refresh_token em texto plano (em produção, usar criptografia)
      refresh_token: token.refresh_token || null,
    });
  } catch (error) {
    console.error('Erro ao salvar token:', error);
    throw error;
  }
}

/**
 * Busca token do servidor PHP/MySQL
 */
export async function getToken(userId: string): Promise<GoogleCalendarToken | null> {
  try {
    const snapshot = await get(ref(database, `users/${userId}/googleCalendarToken`));
    if (!snapshot.exists()) {
      return null;
    }

    const token = snapshot.val() as GoogleCalendarToken;
    
    // Verificar se token expirou
    if (token.expires_at && Date.now() >= token.expires_at) {
      // Tentar renovar token
      if (token.refresh_token) {
        return await refreshToken(userId, token.refresh_token);
      }
      return null;
    }

    return token;
  } catch (error) {
    console.error('Erro ao buscar token:', error);
    return null;
  }
}

/**
 * Renova token usando refresh_token via endpoint PHP seguro
 */
async function refreshToken(userId: string, refreshToken: string): Promise<GoogleCalendarToken | null> {
  try {
    // Usar endpoint PHP seguro no backend para manter client_secret protegido
    const response = await fetch(`${API_BASE_URL}/google-oauth-refresh.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('Erro ao renovar token:', error);
      // Remover token inválido
      await removeToken(userId);
      return null;
    }

    const data = await response.json();
    const token: GoogleCalendarToken = {
      access_token: data.access_token,
      refresh_token: refreshToken, // Manter refresh_token original
      expires_in: data.expires_in,
      expires_at: Date.now() + (data.expires_in * 1000),
      token_type: data.token_type || 'Bearer',
      scope: data.scope || GOOGLE_SCOPES,
    };

    await saveToken(userId, token);
    return token;
  } catch (error) {
    console.error('Erro ao renovar token:', error);
    await removeToken(userId);
    return null;
  }
}

/**
 * Remove token do servidor PHP/MySQL
 */
export async function removeToken(userId: string): Promise<void> {
  try {
    await remove(ref(database, `users/${userId}/googleCalendarToken`));
  } catch (error) {
    console.error('Erro ao remover token:', error);
  }
}

/**
 * Verifica se usuário está autenticado
 */
export async function isAuthenticated(userId: string): Promise<boolean> {
  const token = await getToken(userId);
  return token !== null;
}

/**
 * Inicia fluxo de autenticação OAuth
 */
export function initiateAuth(userId: string): void {
  const authUrl = generateAuthUrl(userId);
  window.location.href = authUrl;
}

/**
 * Processa callback OAuth
 */
export async function handleOAuthCallback(
  code: string | null,
  state: string | null,
  error: string | null
): Promise<{ success: boolean; userId?: string; error?: string }> {
  // Tratar erros do Google OAuth
  if (error) {
    let errorMessage = 'Erro ao autenticar com Google Calendar';
    
    switch (error) {
      case 'access_denied':
        errorMessage = 'Acesso negado. Você precisa conceder permissão para usar o Google Calendar.';
        break;
      case 'invalid_request':
        errorMessage = 'Requisição inválida. Verifique as configurações do Google Calendar.';
        break;
      case 'unauthorized_client':
        errorMessage = 'Cliente não autorizado. Verifique as configurações do Google Cloud Console.';
        break;
      case 'unsupported_response_type':
        errorMessage = 'Tipo de resposta não suportado.';
        break;
      case 'invalid_scope':
        errorMessage = 'Escopo inválido. Verifique as permissões do Google Calendar.';
        break;
      case 'server_error':
        errorMessage = 'Erro no servidor do Google. Tente novamente mais tarde.';
        break;
      case 'temporarily_unavailable':
        errorMessage = 'Serviço temporariamente indisponível. Tente novamente mais tarde.';
        break;
      default:
        errorMessage = `Erro de autenticação: ${error}`;
    }
    
    return { success: false, error: errorMessage };
  }

  if (!code || !state) {
    return { success: false, error: 'Código de autorização ou state não fornecidos. Tente conectar novamente.' };
  }

  try {
    const stateData = JSON.parse(atob(state));
    const token = await exchangeCodeForToken(code, state);
    
    // Verificar se refresh_token foi obtido
    if (!token.refresh_token) {
      console.warn('⚠️ Refresh token não obtido. O usuário precisará reconectar quando o token expirar.');
    }
    
    return { success: true, userId: stateData.userId };
  } catch (error: any) {
    console.error('Erro no callback OAuth:', error);
    return { 
      success: false, 
      error: error.message || 'Erro ao processar autenticação do Google Calendar. Tente novamente.' 
    };
  }
}
